import { useMemo, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { GridHookForm, Typogaphy } from 'vnetwork-ui'
import { yupResolver } from '@hookform/resolvers/yup'

import CustomerSelect from '@/components/Form/Shared/CustomerSelect'
import SubHeaders from '@/components/Headers/SubHeaders'
import Paper from '@/components/Paper'
import TextEditorCustomize from '@/components/TextEditorCustomize'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useFormPersistence } from '@/hooks/useFormPersistence'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { getCustomerById } from '@/modules/Customer/services'
import { createReportWaf } from '../../services'
import { CreateReportPayload } from '../../types/payload'
import {
    providerOptions,
    sendFormatOptions,
    sendFrequencyOptions,
    domainSourceOptions,
    statusOptions,
    engineOptions,
    engineForProvider,
} from '../../types/utils'
import DomainsFilterCard from '../shared/DomainsFilterCard'
import ReportServiceSelect from '../shared/ReportServiceSelect'
import ServiceReportMailerHint from '../shared/ServiceReportMailerHint'
import { CreateReportSchema } from './yup'

interface ReportWafCreateProps {}

// Extends the API payload with a hidden form-only boolean that the yup
// .test on service_id reads to enforce conditional-required binding.
type CreateFormValues = CreateReportPayload & { _requireBinding?: boolean }

const CreateForm: React.FC<ReportWafCreateProps> = () => {
    const navigate = useNavigate()
    const { t } = useTranslation('report_waf')
    const { refetch } = useQueryClientCallback({
        pattern: 'report-waf',
        mode: 'contains',
    })

    // requireBinding is true when the customer has >= 2 bindable
    // service.report mailers — set via onMeta callback from ReportServiceSelect.
    const [requireBinding, setRequireBinding] = useState(false)

    const defaultValues: CreateFormValues = useMemo(
        () => ({
            provider: 'mlytics',
            report_engine: 'v1',
            customer_id: 0,
            customer_name: '',
            description: '',
            priority: 5,
            send_format: 'default',
            send_frequency: 'weekly',
            status: 'activate',
            service_customer_id: '',
            domain_source: 'elastic',
            domains: [],
            service_id: null,
            _requireBinding: false,
        }),
        []
    )

    const formControl = useForm<CreateFormValues>({
        defaultValues,
        resolver: yupResolver(CreateReportSchema) as any,
    })

    useFormPersistence(formControl, 'report-waf-create', defaultValues)

    // Auto-align engine when provider changes. Conversant implies v2,
    // anything else implies v1. Admin can still override manually after.
    const provider = formControl.watch('provider')
    useEffect(() => {
        if (!provider) return
        formControl.setValue('report_engine', engineForProvider(provider))
    }, [provider])

    // Keep the hidden _requireBinding field in sync with the state
    // derived from ReportServiceSelect's onMeta so the yup .test sees
    // the latest value at validation time.
    useEffect(() => {
        formControl.setValue('_requireBinding', requireBinding)
    }, [requireBinding])

    // Auto-fill customer name (company name preferred) when customer_id
    // changes. Recipients are NOT set here anymore — after plan 12 P1 the
    // worker reads them from the customer's `service.report`-scoped mailer
    // at send time, so the form no longer carries an emails field. The
    // current recipient list is surfaced read-only via the
    // <ServiceReportMailerHint> block below with a deep-link to edit at
    // Customer > Mailers.
    const customerId = formControl.watch('customer_id')
    useEffect(() => {
        if (!customerId || customerId === 0) return
        getCustomerById(customerId)
            .then((response) => {
                const customer = response?.result
                if (customer) {
                    const displayName =
                        customer.company?.company_name || customer.customer_name
                    formControl.setValue('customer_name', displayName)
                }
            })
            .catch((error) => {
                console.error('Failed to fetch customer details:', error)
            })
    }, [customerId])

    const { mutateAsync: callCreateReport, isPending: isCreating } =
        useMutationWithHandler(
            { mutationFn: createReportWaf },
            {
                reactQueryKeys: QUERY_KEYS.REPORT_WAF,
                successMessage: t('create_success'),
                errorMessage: t('create_error'),
                onSuccess: () => {
                    refetch()
                    navigate('/technical/report', { replace: true })
                },
            }
        )

    const handleCreateReport = async (values: CreateFormValues) => {
        // Source-of-truth guard: yup .test on service_id is the primary
        // check, but this catches any race where _requireBinding sync
        // lags behind validation.
        if (requireBinding && !values.service_id) {
            formControl.setError('service_id', {
                type: 'manual',
                message: t('bound_service.required', {
                    defaultValue:
                        'A bound service is required — this customer has multiple service.report mailers. Select one to avoid ambiguous delivery.',
                }),
            })
            return
        }

        const serviceCustomerId = values.service_customer_id?.trim()
        const description = values.description?.trim()

        const payload: CreateReportPayload = {
            provider: values.provider,
            report_engine: values.report_engine,
            customer_id: values.customer_id,
            customer_name: values.customer_name.trim(),
            description: description || undefined,
            priority: values.priority,
            send_format: values.send_format,
            send_frequency: values.send_frequency,
            status: values.status,
            service_customer_id: serviceCustomerId || undefined,
            domain_source: values.domain_source,
            domains: values.domains?.length
                ? values.domains.map((domain: string) => domain.trim())
                : undefined,
            service_id: values.service_id ?? null,
        }

        await callCreateReport(payload)
    }

    const currentProvider = formControl.watch('provider') || 'service'

    return (
        <>
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    { path: '/technical', title: 'Technical' },
                    {
                        path: '/technical/report',
                        title: t('list_title'),
                    },
                    {
                        path: '/technical/report/new',
                        title: t('form_title'),
                    },
                ]}
            />
            <Paper className="mt-2">
                <Typogaphy className="mb-4 font-medium text-primary">
                    {t('form_title')}
                </Typogaphy>

                <GridHookForm
                    formControl={formControl}
                    data={[
                        // GridHookForm's internal container is `vnw-grid-4`
                        // = 4-col CSS grid at ≥1024px (2-col 768-1023, 1-col
                        // below). All col-span values below are scaled to
                        // that 4-track base so each row sums to exactly 4
                        // and no item wraps unexpectedly. Full-width rows
                        // use col-span-4; two-thirds use col-span-3;
                        // thirds don't exist in this grid so halves
                        // (col-span-2) are the de-facto "half-width"
                        // baseline.

                        // Row 1 — Identity (full width so long company
                        // names don't truncate)
                        {
                            formItemProps: {
                                name: 'customer_name',
                                label: t('fields.customer_name'),
                                type: 'textfield',
                                size: 'small',
                                placeholder: t(
                                    'report_waf.placeholders.customer_name'
                                ),
                            },
                            className: 'col-span-4',
                        },
                        // Row 2 — Description (rich editor, full width).
                        // TextEditorCustomize adds `mt-2` (8px) on its own
                        // body when a label is present, which stacks on
                        // top of GridHookForm's row-gap and makes this
                        // row sit farther from Customer Name than any
                        // other pair of rows. Cancel it with `-mt-2` on
                        // the wrapper so the gap matches the rest.
                        {
                            renderEditComponent: () => (
                                <div className="-mt-2">
                                    <TextEditorCustomize
                                        label={t('fields.description')}
                                        placeholder={t(
                                            'report_waf.placeholders.description'
                                        )}
                                        value={{
                                            text:
                                                formControl.watch(
                                                    'description'
                                                ) || '',
                                            attachments: [],
                                        }}
                                        onChange={(value) =>
                                            formControl.setValue(
                                                'description',
                                                value.text
                                            )
                                        }
                                        readOnly={false}
                                    />
                                </div>
                            ),
                            className: 'col-span-4',
                        },
                        // Row 3 — Backend binding. Customer ID dominates
                        // (col-span-2) because it shows the customer
                        // email+name; provider/engine are short selects.
                        {
                            className: 'col-span-2',
                            renderEditComponent: (control) => (
                                <CustomerSelect
                                    control={control}
                                    name="customer_id"
                                    label={t('fields.customer_id')}
                                />
                            ),
                        },
                        {
                            className: 'col-span-1',
                            formItemProps: {
                                name: 'provider',
                                label: t('fields.provider'),
                                type: 'select',
                                size: 'small',
                                options: providerOptions(t),
                            },
                        },
                        {
                            className: 'col-span-1',
                            formItemProps: {
                                name: 'report_engine',
                                label: t('fields.report_engine'),
                                type: 'select',
                                size: 'small',
                                options: engineOptions(t),
                            },
                        },
                        // Row 4 — provider-scoped customer id gets half
                        // the row (values like 15523 or 138762883534869340
                        // need space), paired with send_format on the right.
                        {
                            className: 'col-span-2',
                            formItemProps: {
                                name: 'service_customer_id',
                                label: `${currentProvider}_id`,
                                type: 'textfield',
                                size: 'small',
                                placeholder: t(
                                    'report_waf.placeholders.service_customer_id'
                                ),
                            },
                        },
                        {
                            formItemProps: {
                                name: 'send_format',
                                label: t('fields.send_format'),
                                type: 'select',
                                size: 'small',
                                options: sendFormatOptions(t),
                            },
                            className: 'col-span-2',
                        },
                        // Row 5 — Schedule knobs (frequency + status,
                        // halves)
                        {
                            formItemProps: {
                                name: 'send_frequency',
                                label: t('fields.send_frequency'),
                                type: 'select',
                                size: 'small',
                                options: sendFrequencyOptions(t),
                            },
                            className: 'col-span-2',
                        },
                        {
                            formItemProps: {
                                name: 'status',
                                label: t('fields.status'),
                                type: 'select',
                                size: 'small',
                                options: statusOptions(t),
                            },
                            className: 'col-span-2',
                        },
                        // Row 6 — Priority + domain source (halves)
                        {
                            formItemProps: {
                                name: 'priority',
                                label: t('priority_level'),
                                type: 'number',
                                size: 'small',
                                placeholder: t('priority_helper'),
                                helperText: t('priority_helper'),
                            },
                            className: 'col-span-2',
                        },
                        {
                            formItemProps: {
                                name: 'domain_source',
                                label: t('fields.domain_source'),
                                type: 'select',
                                size: 'small',
                                options: domainSourceOptions(t),
                            },
                            className: 'col-span-2',
                        },
                        // Row 7 — Recipients surface (read-only mailer hint).
                        // Styled as a bordered card with icon + optional
                        // deep-link to Customer > Mailers.
                        {
                            renderEditComponent: () => (
                                <ServiceReportMailerHint
                                    customerId={customerId}
                                    serviceId={formControl.watch('service_id')}
                                />
                            ),
                            className: 'col-span-4',
                        },
                        // Row 7b — Service binding picker. Lets admin pick
                        // which of the customer's services this report is
                        // bound to; the V2 worker uses the chosen
                        // `service_id` to filter the mailer search down to
                        // the correct group when the customer has multiple
                        // `service.report` mailers.
                        {
                            renderEditComponent: () => (
                                <div>
                                    <ReportServiceSelect
                                        customerId={customerId}
                                        value={formControl.watch('service_id')}
                                        onChange={(id) =>
                                            formControl.setValue(
                                                'service_id',
                                                id
                                            )
                                        }
                                        onMeta={(meta) =>
                                            setRequireBinding(
                                                meta.requireBinding
                                            )
                                        }
                                    />
                                    {formControl.formState.errors
                                        .service_id && (
                                        <p className="text-xs text-destructive mt-1">
                                            {
                                                formControl.formState.errors
                                                    .service_id.message
                                            }
                                        </p>
                                    )}
                                </div>
                            ),
                            className: 'col-span-4',
                        },
                        // Row 8 — Domains filter card, matching the
                        // Recipients card's border/padding so the two
                        // "scope" sections read as a visual pair. Optional
                        // field kept last so the mandatory inputs flow
                        // top-to-bottom uninterrupted.
                        {
                            renderEditComponent: (control) => (
                                <DomainsFilterCard control={control} />
                            ),
                            className: 'col-span-4',
                        },
                    ]}
                    actions={[
                        {
                            title: t('actions.cancel'),
                            type: 'button',
                            variant: 'outlined',
                            handler: () => navigate('/technical/report'),
                        },
                        {
                            title: t('buttons.create'),
                            type: 'submit',
                            variant: 'contained',
                            loading: isCreating,
                            disabled: isCreating,
                        },
                    ]}
                    onSubmit={handleCreateReport}
                />
            </Paper>
        </>
    )
}

export const ReportWafCreate = (props: ReportWafCreateProps) => {
    return <CreateForm {...props} />
}

export default ReportWafCreate
